import { ArrowUp, Atom02, MessageChatCircle, User01 } from "@untitledui/icons";

/**
 * Illustrazioni decorative (solo CSS) per le slide onboarding Shipper.
 * `aria-hidden`: il contenuto è spiegato da titolo + corpo della slide.
 */
export type AskEveMockLabels = {
    eveName: string;
    assistantSubtitle: string;
    typingStatus: string;
    yourMessage: string;
    eveReply: string;
    composeTitle: string;
    placeholder: string;
    send: string;
};

type ShipperOnboardingSlideMockProps = {
    variant?: string;
    /** Etichette per i mock chat Ask Eve (solo sezione ask-eve). */
    askEveLabels?: AskEveMockLabels | null;
};

export function ShipperOnboardingSlideMock({ variant, askEveLabels }: ShipperOnboardingSlideMockProps) {
    if (!variant) return null;

    const L = askEveLabels;

    const shell =
        "onboarding-mock-root rounded-xl border border-secondary bg-secondary/40 p-3 ring-1 ring-secondary/60";
    const bar = "h-2 rounded-full bg-quaternary/25";
    /** Linee skeleton più visibili su sfondo scuro */
    const line = "h-2 rounded-full bg-quaternary/55";

    switch (variant) {
        case "eve-chat-intro":
            return (
                <div className={`${shell} space-y-3`} aria-hidden="true">
                    <div className="flex items-center gap-2.5 border-b border-secondary pb-2.5">
                        <div className="onboarding-mock-soft-pulse flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-solid/20 ring-1 ring-brand-solid/40">
                            <Atom02 className="size-5 text-brand-secondary" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold text-primary">{L?.eveName ?? "Eve"}</p>
                            <p className="truncate text-[10px] text-tertiary">{L?.assistantSubtitle ?? ""}</p>
                        </div>
                        <MessageChatCircle className="size-5 shrink-0 text-quaternary" />
                    </div>
                    <div className="flex items-center justify-center gap-2 rounded-lg bg-primary/50 py-2 ring-1 ring-secondary/80">
                        <span className="text-[10px] font-medium uppercase tracking-wide text-tertiary">
                            {L?.typingStatus ?? "…"}
                        </span>
                        <div className="flex items-center gap-1">
                            <span className="onboarding-mock-typing-dot size-2 rounded-full bg-brand-solid" />
                            <span className="onboarding-mock-typing-dot size-2 rounded-full bg-brand-solid" />
                            <span className="onboarding-mock-typing-dot size-2 rounded-full bg-brand-solid" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-brand-solid/20 ring-1 ring-brand-solid/35">
                            <Atom02 className="size-4 text-brand-secondary" />
                        </div>
                        <div className="onboarding-mock-bubble-breathe min-w-0 flex-1 rounded-xl rounded-tl-md bg-brand-solid/12 px-3 py-2.5 ring-1 ring-brand-solid/35">
                            <p className="mb-2 text-[10px] font-semibold text-brand-secondary">{L?.eveName ?? "Eve"}</p>
                            <div className={`${line} onboarding-mock-shimmer-bar w-full`} />
                            <div className={`${line} onboarding-mock-shimmer-bar mt-2 w-[88%]`} />
                        </div>
                    </div>
                </div>
            );

        case "eve-context-cards":
            return (
                <div className={`${shell}`} aria-hidden="true">
                    <div className="grid grid-cols-3 gap-2">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="onboarding-mock-card-stagger rounded-lg bg-primary p-2 ring-1 ring-secondary">
                                <div className={`${bar} onboarding-mock-shimmer-bar w-8`} />
                                <div className={`${bar} onboarding-mock-shimmer-bar mt-2 w-full`} />
                                <div className={`${bar} onboarding-mock-shimmer-bar mt-1 w-2/3`} />
                            </div>
                        ))}
                    </div>
                </div>
            );

        case "eve-thread":
            return (
                <div className={`${shell} space-y-3`} aria-hidden="true">
                    <div className="flex flex-col items-end gap-1.5">
                        <div className="flex items-center gap-1.5 pr-0.5 text-[10px] font-semibold text-tertiary">
                            <span>{L?.yourMessage ?? "You"}</span>
                            <span className="flex size-6 items-center justify-center rounded-full bg-quaternary/30 ring-1 ring-secondary">
                                <User01 className="size-3.5 text-primary" />
                            </span>
                        </div>
                        <div className="onboarding-mock-thread-user w-full max-w-[92%] rounded-xl rounded-tr-md border border-secondary bg-quaternary/25 px-3 py-2.5 shadow-xs ring-1 ring-white/5">
                            <div className={`${line} onboarding-mock-shimmer-bar w-full`} />
                            <div className={`${line} onboarding-mock-shimmer-bar mt-2 w-[58%]`} />
                        </div>
                    </div>
                    <div className="relative flex justify-center py-0.5">
                        <div className="h-px w-full max-w-[70%] bg-gradient-to-r from-transparent via-quaternary/40 to-transparent" />
                    </div>
                    <div className="flex flex-col items-start gap-1.5">
                        <div className="flex items-center gap-1.5 pl-0.5 text-[10px] font-semibold text-brand-secondary">
                            <span className="flex size-6 items-center justify-center rounded-full bg-brand-solid/20 ring-1 ring-brand-solid/35">
                                <Atom02 className="size-3.5 text-brand-secondary" />
                            </span>
                            <span>{L?.eveReply ?? "Eve"}</span>
                        </div>
                        <div className="flex w-full max-w-[95%] gap-2">
                            <div className="onboarding-mock-thread-assistant min-w-0 flex-1 rounded-xl rounded-tl-md border border-brand-solid/25 bg-brand-solid/12 px-3 py-2.5 shadow-xs ring-1 ring-brand-solid/25">
                                <div className={`${line} onboarding-mock-shimmer-bar w-full`} />
                                <div className={`${line} onboarding-mock-shimmer-bar mt-2 w-[94%]`} />
                                <div className={`${line} onboarding-mock-shimmer-bar mt-2 w-[72%]`} />
                            </div>
                        </div>
                    </div>
                </div>
            );

        case "eve-compose":
            return (
                <div className={`${shell} space-y-2`} aria-hidden="true">
                    <div className="flex items-center gap-2">
                        <MessageChatCircle className="size-4 shrink-0 text-quaternary" />
                        <span className="text-[11px] font-semibold text-primary">{L?.composeTitle ?? "Message"}</span>
                    </div>
                    <div className="rounded-xl border border-secondary bg-primary p-2.5 ring-1 ring-secondary/80">
                        <div className="onboarding-mock-scan-wrap relative min-h-[52px] overflow-hidden rounded-lg bg-secondary/80 px-3 py-2 ring-1 ring-secondary">
                            <span className="onboarding-mock-scan-line" />
                            <p className="mb-2 text-[11px] text-tertiary/90">{L?.placeholder ?? "…"}</p>
                            <div className="flex items-center gap-2 border-t border-secondary/80 pt-2">
                                <div className={`${line} min-w-0 flex-1 opacity-70`} />
                                <span className="onboarding-mock-caret shrink-0 text-tertiary" />
                            </div>
                        </div>
                        <div className="mt-2.5 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-[10px] text-quaternary">
                                <Atom02 className="size-3.5 text-brand-secondary" />
                                <span>{L?.eveName ?? "Eve"}</span>
                            </div>
                            <div className="flex flex-col items-center gap-0.5">
                                <div className="onboarding-mock-send-pulse flex size-9 items-center justify-center rounded-lg bg-brand-solid shadow-xs ring-1 ring-brand-solid/50">
                                    <ArrowUp className="size-4 text-white" />
                                </div>
                                <span className="text-[9px] font-medium text-tertiary">{L?.send ?? "Send"}</span>
                            </div>
                        </div>
                    </div>
                </div>
            );

        case "hub-hero-cards":
            return (
                <div className={`${shell}`} aria-hidden="true">
                    <div className="grid grid-cols-2 gap-2">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="onboarding-mock-soft-pulse rounded-lg bg-primary p-3 ring-1 ring-secondary">
                                <div className={`${bar} onboarding-mock-shimmer-bar w-12`} />
                                <div className="mt-3 text-2xl font-semibold tabular-nums text-tertiary">##</div>
                            </div>
                        ))}
                    </div>
                </div>
            );

        case "hub-kpis":
            return (
                <div className={`${shell} space-y-3`} aria-hidden="true">
                    {[72, 48, 88].map((w, i) => (
                        <div key={i} className="space-y-1">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-24`} />
                            <div className="h-2 w-full overflow-hidden rounded-full bg-quaternary/15">
                                <div
                                    className="onboarding-mock-grow-x h-full rounded-full bg-brand-solid/50"
                                    style={{ width: `${w}%`, animationDelay: `${i * 0.25}s` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            );

        case "hub-shortcuts":
            return (
                <div className={`${shell} flex gap-3`} aria-hidden="true">
                    <div className="flex w-14 flex-col gap-1.5 rounded-lg bg-primary p-1.5 ring-1 ring-secondary">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className={`h-6 rounded ${i === 2 ? "onboarding-mock-soft-pulse bg-brand-solid/20 ring-1 ring-brand-solid/30" : "bg-quaternary/10"}`}
                            />
                        ))}
                    </div>
                    <div className="flex flex-1 items-center justify-center">
                        <span className="onboarding-mock-nudge-arrow text-2xl text-tertiary">→</span>
                    </div>
                    <div className="min-h-[88px] flex-1 rounded-lg bg-primary p-2 ring-1 ring-secondary">
                        <div className={`${bar} onboarding-mock-shimmer-bar w-2/3`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-2 w-full`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-2 w-4/5`} />
                    </div>
                </div>
            );

        case "hub-ready":
            return (
                <div className={`${shell} flex flex-col items-center justify-center gap-2 py-4`} aria-hidden="true">
                    <div className="onboarding-mock-check-pop flex size-14 items-center justify-center rounded-full bg-success-solid/15 ring-2 ring-success-solid/40">
                        <span className="text-2xl text-success-solid">✓</span>
                    </div>
                    <div className={`${bar} onboarding-mock-shimmer-bar w-32`} />
                </div>
            );

        case "ops-modules":
            return (
                <div className={`${shell}`} aria-hidden="true">
                    <div className="grid grid-cols-3 gap-2">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="onboarding-mock-card-stagger rounded-lg bg-primary p-2 ring-1 ring-secondary">
                                <div className="onboarding-mock-soft-pulse mx-auto mb-2 size-8 rounded-lg bg-quaternary/15 ring-1 ring-secondary" />
                                <div className={`${bar} onboarding-mock-shimmer-bar w-full`} />
                                <div className={`${bar} onboarding-mock-shimmer-bar mt-1.5 w-2/3`} />
                            </div>
                        ))}
                    </div>
                </div>
            );

        case "ops-route":
            return (
                <div className={`${shell} py-4`} aria-hidden="true">
                    <div className="flex items-center justify-between gap-1 px-2">
                        <div className="size-3 rounded-full bg-quaternary/40 ring-2 ring-secondary" />
                        <div
                            className="onboarding-mock-route-line h-px flex-1 bg-gradient-to-r from-quaternary/30 via-brand-solid/40 to-quaternary/30"
                            style={{ animationDelay: "0s" }}
                        />
                        <div className="onboarding-mock-dot-pulse size-4 rounded-full bg-brand-solid/60 ring-2 ring-brand-solid/25" />
                        <div
                            className="onboarding-mock-route-line h-px flex-1 bg-gradient-to-r from-quaternary/30 via-brand-solid/40 to-quaternary/30"
                            style={{ animationDelay: "0.35s" }}
                        />
                        <div className="size-3 rounded-full bg-quaternary/40 ring-2 ring-secondary" />
                    </div>
                </div>
            );

        case "ops-files":
            return (
                <div className={`${shell} space-y-2`} aria-hidden="true">
                    <div className="onboarding-mock-scan-wrap relative flex items-center gap-2 overflow-hidden rounded-lg bg-primary p-2 ring-1 ring-secondary">
                        <span className="onboarding-mock-scan-line" />
                        <div className="size-8 shrink-0 rounded-md bg-utility-warning/15 ring-1 ring-utility-warning/30" />
                        <div className="flex-1 space-y-1">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-28`} />
                            <div className={`${bar} w-16 opacity-60`} />
                        </div>
                    </div>
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="onboarding-mock-feed-row flex items-center gap-2 rounded-md bg-primary/80 px-2 py-1.5 ring-1 ring-secondary/80"
                        >
                            <div className="size-2 rounded-sm bg-quaternary/40" />
                            <div className={`${bar} onboarding-mock-shimmer-bar flex-1`} />
                        </div>
                    ))}
                </div>
            );

        case "ops-nav-hint":
            return (
                <div className={`${shell} flex gap-2`} aria-hidden="true">
                    <div className="w-24 space-y-1 rounded-lg bg-primary p-1.5 ring-1 ring-secondary">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className={`rounded px-2 py-2 ${i === 2 ? "onboarding-mock-soft-pulse bg-brand-solid/15 ring-1 ring-brand-solid/25" : "bg-quaternary/5"}`}
                            >
                                <div className={`${bar} onboarding-mock-shimmer-bar w-full ${i === 2 ? "opacity-80" : "opacity-40"}`} />
                            </div>
                        ))}
                    </div>
                    <div className="min-h-[100px] flex-1 rounded-lg bg-primary p-2 ring-1 ring-secondary">
                        <div className={`${bar} onboarding-mock-shimmer-bar w-1/2`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-3 w-full`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-2 w-4/5`} />
                    </div>
                </div>
            );

        case "coord-pillars":
            return (
                <div className={`${shell} grid grid-cols-3 gap-2`} aria-hidden="true">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="onboarding-mock-card-stagger rounded-lg bg-primary p-2 ring-1 ring-secondary">
                            <div
                                className={`mx-auto mb-2 h-8 w-8 rounded-md ${i === 1 ? "onboarding-mock-soft-pulse bg-error-primary/15" : "bg-quaternary/15"} ring-1 ring-secondary`}
                            />
                            <div className={`${bar} onboarding-mock-shimmer-bar w-full`} />
                            <div className={`${bar} onboarding-mock-shimmer-bar mt-1 w-3/4`} />
                        </div>
                    ))}
                </div>
            );

        case "coord-feed":
            return (
                <div className={`${shell} space-y-2`} aria-hidden="true">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="onboarding-mock-feed-row flex gap-2 rounded-lg bg-primary p-2 ring-1 ring-secondary">
                            <div className="size-2 shrink-0 rounded-full bg-brand-solid/50" />
                            <div className="flex-1 space-y-1">
                                <div className={`${bar} onboarding-mock-shimmer-bar w-full`} />
                                <div className={`${bar} w-1/3 opacity-50`} />
                            </div>
                            <div className={`${bar} w-10 shrink-0 opacity-40`} />
                        </div>
                    ))}
                </div>
            );

        case "coord-tickets":
            return (
                <div className={`${shell} space-y-2`} aria-hidden="true">
                    <div className="flex items-center gap-2">
                        <span className="onboarding-mock-soft-pulse rounded-full bg-error-primary/15 px-2 py-0.5 text-[10px] font-semibold text-error-primary ring-1 ring-error-primary/30">
                            2
                        </span>
                        <div className={`${bar} onboarding-mock-shimmer-bar flex-1`} />
                    </div>
                    {[1, 2].map((i) => (
                        <div key={i} className="rounded-lg border border-error-primary/20 bg-error-primary/5 p-2">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-4/5`} />
                            <div className={`${bar} onboarding-mock-shimmer-bar mt-1.5 w-1/2`} />
                        </div>
                    ))}
                </div>
            );

        case "coord-rhythm":
            return (
                <div className={`${shell} flex items-end justify-between gap-1 px-2 pb-1 pt-3`} aria-hidden="true">
                    {[40, 65, 45, 80, 55, 70, 50].map((h, i) => (
                        <div key={i} className="flex w-full flex-col items-center gap-1">
                            <div
                                className="onboarding-mock-bar-wave w-full max-w-[14px] rounded-t-sm bg-brand-solid/35 ring-1 ring-brand-solid/25"
                                style={{ height: `${h}px`, animationDelay: `${i * 0.08}s` }}
                            />
                            <div className="h-1 w-1 rounded-full bg-quaternary/40" />
                        </div>
                    ))}
                </div>
            );

        case "comm-bridge":
            return (
                <div className={`${shell} flex items-center gap-2`} aria-hidden="true">
                    <div className="flex-1 rounded-lg bg-primary p-3 ring-1 ring-secondary">
                        <div className={`${bar} onboarding-mock-shimmer-bar w-16`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-2 w-full`} />
                    </div>
                    <span className="onboarding-mock-nudge-arrow text-lg text-tertiary">→</span>
                    <div className="flex-1 rounded-lg bg-primary p-3 ring-1 ring-brand-solid/25">
                        <div className={`${bar} onboarding-mock-shimmer-bar w-20`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-2 w-full`} />
                    </div>
                </div>
            );

        case "comm-order-card":
            return (
                <div className={`${shell}`} aria-hidden="true">
                    <div className="rounded-lg bg-primary p-3 ring-1 ring-secondary">
                        <div className="flex justify-between gap-2">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-24`} />
                            <div className="onboarding-mock-soft-pulse size-6 shrink-0 rounded-md bg-brand-solid/15 ring-1 ring-brand-solid/25" />
                        </div>
                        <div className="mt-3 space-y-2">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-full`} />
                            <div className={`${bar} onboarding-mock-shimmer-bar w-5/6`} />
                            <div className={`${bar} onboarding-mock-shimmer-bar w-2/3`} />
                        </div>
                    </div>
                </div>
            );

        case "comm-quote-compare":
            return (
                <div className={`${shell} grid grid-cols-2 gap-2`} aria-hidden="true">
                    {[1, 2].map((i) => (
                        <div key={i} className="rounded-lg bg-primary p-2 ring-1 ring-secondary">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-12`} />
                            <div className="mt-2 space-y-1">
                                {[85, 60, 90].map((pct, j) => (
                                    <div key={j} className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-success-solid">✓</span>
                                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-quaternary/15">
                                            <div
                                                className="onboarding-mock-grow-x h-full rounded-full bg-brand-solid/45"
                                                style={{ width: `${pct}%`, animationDelay: `${(i - 1) * 0.15 + j * 0.1}s` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            );

        case "comm-handoff":
            return (
                <div className={`${shell} flex items-center justify-center gap-3 py-3`} aria-hidden="true">
                    <div className="rounded-lg bg-primary px-3 py-2 ring-1 ring-secondary">
                        <div className={`${bar} onboarding-mock-shimmer-bar w-16`} />
                    </div>
                    <svg
                        className="onboarding-mock-spin-slow size-8 shrink-0 text-brand-solid/50"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                    >
                        <path
                            d="M5 12a7 7 0 0112.5-4M19 12a7 7 0 01-12.5 4"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                        />
                        <path d="M15 5l4 3-4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 19l-4-3 4-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="rounded-lg bg-primary px-3 py-2 ring-1 ring-secondary">
                        <div className={`${bar} onboarding-mock-shimmer-bar w-16`} />
                    </div>
                </div>
            );

        case "net-graph":
            return (
                <div className={`${shell} relative h-[120px]`} aria-hidden="true">
                    <svg className="absolute inset-0 size-full text-quaternary/30" aria-hidden="true">
                        <line x1="20%" y1="70%" x2="50%" y2="35%" stroke="currentColor" strokeWidth="2" />
                        <line x1="50%" y1="35%" x2="80%" y2="70%" stroke="currentColor" strokeWidth="2" />
                        <line x1="20%" y1="70%" x2="80%" y2="70%" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    {[
                        { l: "25%", t: "68%" },
                        { l: "47%", t: "28%" },
                        { l: "72%", t: "68%" },
                    ].map((p, i) => (
                        <div
                            key={i}
                            className="onboarding-mock-net-node absolute size-8 rounded-full bg-primary ring-2 ring-brand-solid/30"
                            style={{ left: p.l, top: p.t, animationDelay: `${i * 0.25}s` }}
                        />
                    ))}
                </div>
            );

        case "net-partner-cards":
            return (
                <div className={`${shell} grid grid-cols-2 gap-2`} aria-hidden="true">
                    {[1, 2].map((i) => (
                        <div key={i} className="onboarding-mock-card-stagger rounded-lg bg-primary p-2 ring-1 ring-secondary">
                            <div className="flex items-center gap-2">
                                <div className="size-8 rounded-md bg-quaternary/15 ring-1 ring-secondary" />
                                <div className="flex-1 space-y-1">
                                    <div className={`${bar} onboarding-mock-shimmer-bar w-full`} />
                                    <div className={`${bar} w-2/3 opacity-60`} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );

        case "net-team-row":
            return (
                <div className={`${shell} flex items-center justify-between gap-3`} aria-hidden="true">
                    <div className="flex -space-x-2">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="onboarding-mock-soft-pulse size-9 rounded-full bg-gradient-to-br from-quaternary/30 to-quaternary/10 ring-2 ring-primary"
                                style={{ animationDelay: `${(i - 1) * 0.15}s` }}
                            />
                        ))}
                    </div>
                    <div className="onboarding-mock-soft-pulse flex size-10 items-center justify-center rounded-lg bg-quaternary/15 ring-1 ring-secondary">
                        <svg className="size-5 text-tertiary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M7 11V8a5 5 0 0110 0v3"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                            <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </div>
                </div>
            );

        case "net-contact-card":
            return (
                <div className={`${shell} space-y-2`} aria-hidden="true">
                    <div className="flex items-start gap-2">
                        <div className="onboarding-mock-soft-pulse size-10 rounded-lg bg-quaternary/15 ring-1 ring-secondary" />
                        <div className="flex-1 space-y-1">
                            <div className={`${bar} onboarding-mock-shimmer-bar w-32`} />
                            <div className={`${bar} w-48 opacity-70`} />
                        </div>
                    </div>
                    <div className="onboarding-mock-scan-wrap relative overflow-hidden rounded-md bg-secondary/60 px-2 py-1.5">
                        <span className="onboarding-mock-scan-line" />
                        <div className={`${bar} onboarding-mock-shimmer-bar w-full`} />
                        <div className={`${bar} onboarding-mock-shimmer-bar mt-1 w-4/5`} />
                    </div>
                </div>
            );

        default:
            return null;
    }
}
